import { screenService } from "@/services/screen/screen.service";
import {
    LedControllerPairRequestDto,
    PageableSearchOptions,
    ScreenDto,
    UpdateScreenConfigurationDto,
} from "@exibra/shared";
import {
    type QueryClient,
    type QueryKey,
    useMutation,
    useQuery,
    useQueryClient,
} from "@tanstack/react-query";
import isNil from "lodash.isnil";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

const SCREEN_STATUS_REFETCH_INTERVAL_MS = 15_000;

export const screenApiQueryKeys = {
    screens: (searchOptions: PageableSearchOptions) =>
        `screens-${searchOptions.page}-${searchOptions.pageSize}-${searchOptions.tenantId}-${searchOptions.search}-${searchOptions.sortingKey}-${searchOptions.sortingDirection}`,
    screen: (id: number) => `screen-${id}`,
    ledController: (id: number) => `screen-${id}-led-controller`,
    nextSerialNumber: (tenantId: number) => `next-serial-number-${tenantId}`,
} as const;

/**
 * Returns whether a TanStack query key belongs to any paginated screen list query.
 */
export const isScreensQueryKey = (queryKey: QueryKey) => {
    return typeof queryKey[0] === "string" && queryKey[0].startsWith("screens-");
};

/**
 * Invalidates all screen list queries and, when known, one screen detail query.
 */
export const invalidateScreenQueries = (queryClient: QueryClient, screenId?: number) => {
    queryClient.invalidateQueries({
        predicate: (query) => isScreensQueryKey(query.queryKey),
    });

    if (!isNil(screenId)) {
        queryClient.invalidateQueries({ queryKey: [screenApiQueryKeys.screen(screenId)] });
    }
};

/**
 * Hook to fetch screens
 * @param options - The options for the query
 * @returns The screens
 */
export const useScreens = (options: PageableSearchOptions) => {
    return useQuery({
        queryKey: [screenApiQueryKeys.screens(options)],
        queryFn: () => screenService.getScreens(options),
        refetchInterval: SCREEN_STATUS_REFETCH_INTERVAL_MS,
    });
};

/**
 * Hook to fetch the next serial number for the tenant
 * @param tenantId - The tenant id
 * @returns The next serial number
 */
export const useNextSerialNumber = (tenantId: number) => {
    return useQuery({
        queryKey: [screenApiQueryKeys.nextSerialNumber, tenantId],
        queryFn: () => screenService.getNextSerialNumber(tenantId),
        enabled: !!tenantId,
    });
};

/**
 * Hook to pair a screen
 * @returns The pair screen mutation
 */
export const usePairScreen = () => {
    return useMutation({
        mutationFn: ({
            uuid,
            tenantId,
            name,
            serialNumber,
        }: {
            uuid: string;
            tenantId: number;
            name: string;
            serialNumber: string;
        }) => screenService.pairScreen(uuid, tenantId, name, serialNumber),
    });
};

/**
 * Hook to fetch a screen by id
 * @param id - The screen id
 * @returns The screen
 */
export const useScreen = (id?: number) => {
    return useQuery({
        queryKey: [screenApiQueryKeys.screen(id!)],
        queryFn: () => screenService.getScreen(id!),
        enabled: !isNil(id),
        refetchInterval: SCREEN_STATUS_REFETCH_INTERVAL_MS,
    });
};

/**
 * Hook to update a screen
 * @returns The update screen mutation
 */
export const useUpdateScreen = () => {
    const { t } = useTranslation();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ id, screen }: { id: number; screen: Partial<ScreenDto> }) =>
            screenService.updateScreen(id, screen),
        onSuccess: (_, variables) => {
            invalidateScreenQueries(queryClient, variables.id);
            toast.success(t("screens.success.updated"));
        },
        onError: (error) => {
            console.error(error);
            toast.error(t("screens.errors.updateFailed"));
        },
    });
};

/**
 * Hook to update screen placeholders
 * @returns The update placeholders mutation
 */
export const useUpdateScreenConfiguration = () => {
    const { t } = useTranslation();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({
            screenId,
            data,
        }: {
            screenId: number;
            data: UpdateScreenConfigurationDto;
        }) => screenService.updateScreenConfiguration(screenId, data),
        onSuccess: (updatedScreen) => {
            if (updatedScreen.id) {
                invalidateScreenQueries(queryClient, updatedScreen.id);
                toast.success(t("screens.success.configurationUpdated"));
            }
        },
        onError: (error) => {
            console.error(error);
            toast.error(t("screens.errors.configurationUpdateFailed"));
        },
    });
};

/**
 * Hook to trigger an immediate presenter update for a screen
 * @returns The presenter update mutation
 */
export const useTriggerPresenterUpdate = () => {
    const { t } = useTranslation();

    return useMutation({
        mutationFn: (screenId: number) => screenService.triggerPresenterUpdate(screenId),
        onSuccess: () => {
            toast.success(t("screens.presenterUpdate.commandAccepted"));
        },
        onError: (error) => {
            console.error(error);
            toast.error(t("screens.presenterUpdate.commandFailed"));
        },
    });
};

/**
 * Hook to fetch local LED controller status from an online presenter.
 * @param screenId The screen whose presenter should report LED state
 * @param enabled Whether polling should be enabled
 * @returns The LED controller status query
 */
export const useLedControllerStatus = (screenId?: number, enabled = true) => {
    return useQuery({
        queryKey: [screenApiQueryKeys.ledController(screenId!)],
        queryFn: () => screenService.getLedControllerStatus(screenId!),
        enabled: enabled && !isNil(screenId),
        refetchInterval: SCREEN_STATUS_REFETCH_INTERVAL_MS,
        retry: false,
        throwOnError: false,
    });
};

/**
 * Hook to scan for LEDnet controllers through a presenter's local Wi-Fi.
 * @returns The LED scan mutation
 */
export const useScanLedControllers = () => {
    return useMutation({
        mutationFn: (screenId: number) => screenService.scanLedControllers(screenId),
    });
};

/**
 * Hook to pair a presenter with a selected LED controller.
 * @returns The LED pairing mutation
 */
export const usePairLedController = () => {
    return useMutation({
        mutationFn: ({
            screenId,
            pairing,
        }: {
            screenId: number;
            pairing: LedControllerPairRequestDto;
        }) => screenService.pairLedController(screenId, pairing),
    });
};

/**
 * Hook to trigger a transient locate (visual identify) on an unpaired LED controller.
 * @returns The LED locate mutation
 */
export const useLocateLedController = () => {
    return useMutation({
        mutationFn: ({
            screenId,
            target,
        }: {
            screenId: number;
            target: LedControllerPairRequestDto;
        }) => screenService.locateLedController(screenId, target),
    });
};

/**
 * Hook to clear a presenter's local LED controller pairing.
 * @returns The LED forget mutation
 */
export const useForgetLedController = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (screenId: number) => screenService.forgetLedController(screenId),
        onSuccess: (_, screenId) => {
            queryClient.invalidateQueries({
                queryKey: [screenApiQueryKeys.ledController(screenId)],
            });
        },
    });
};
